package ui.widget

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.geometry.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.layout.*
import io.*
import kotlinx.coroutines.*
import tk.mallumo.log.*
import tk.mallumo.utils.*
import kotlin.math.*

@Composable
fun LazyImage(
    id: Long,
    modifierPlaceHolder: Modifier,
    modifierContent: Modifier,
    contentScale: ContentScale = ContentScale.Fit
) {
    val imageState = remember(id) {
        mutableDataState<ImageBitmap>()
    }
    Box(modifierPlaceHolder) {
        when (val state = imageState.value) {
            is DataState.Idle -> Unit
            is DataState.Error -> Text("Fetch ERR", Modifier.align(Alignment.Center))
            is DataState.Loading -> {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
            }

            is DataState.Result -> {
                Image(
                    bitmap = state.nnvl,
                    alignment = Alignment.Center,
                    contentDescription = null,
                    contentScale = contentScale,
                    filterQuality = FilterQuality.High,
                    modifier = modifierContent
                )
            }
        }
    }
    LaunchedEffect(id) {
        if (Repository.files.contains(id)) {
            imageState.value = DataState.Result(Repository.files.fetchImage(id))
        } else {
            imageState.value = DataState.Loading()
            runCatching {
                Repository.files.fetchImage(id)
            }.onSuccess {
                if (isActive) imageState.value = DataState.Result(it)
            }.onFailure {
                if (isActive) imageState.value = DataState.Error(it.message ?: "Fetch fail")
            }
        }
    }
}

@Composable
fun LazyImage(id: Long, modifier: Modifier, contentScale: ContentScale = ContentScale.Fit, alphaBackground: Boolean = true) {
    val imageState = remember(id) {
        mutableDataState<ImageBitmap>()
    }
    Box(modifier) {
        when (val state = imageState.value) {
            is DataState.Idle -> Unit
            is DataState.Error -> Text("Fetch ERR", Modifier.align(Alignment.Center))
            is DataState.Loading -> {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
            }

            is DataState.Result -> {
                Image(
                    bitmap = state.nnvl,
                    alignment = Alignment.Center,
                    contentDescription = null,
                    contentScale = contentScale,
                    filterQuality = FilterQuality.High,
                    modifier = Modifier.fillMaxSize()
                        .then(
                            if (alphaBackground) Modifier.drawAlphaBackground()
                            else Modifier
                        )
                )
            }
        }
    }
    LaunchedEffect(id) {
        if (Repository.files.contains(id)) {
            imageState.value = DataState.Result(Repository.files.fetchImage(id))
        } else {
            imageState.value = DataState.Loading()
            runCatching {
                Repository.files.fetchImage(id)
            }.onSuccess {
                if (isActive) imageState.value = DataState.Result(it)
            }.onFailure {
                if (isActive) imageState.value = DataState.Error(it.message ?: "Fetch fail")
            }
        }
    }
}

@Composable
fun LazyImage(
    category: String, key: String,
    modifier: Modifier,
    contentScale: ContentScale = ContentScale.Fit,
    alphaBackground: Boolean = true
) {
    val imageState = remember(category, key) {
        mutableDataState<ImageBitmap>()
    }
    Box(modifier) {
        when (val state = imageState.value) {
            is DataState.Idle -> Unit
            is DataState.Error -> Text("Fetch ERR", Modifier.align(Alignment.Center))
            is DataState.Loading -> {
                CircularProgressIndicator(Modifier.align(Alignment.Center))
            }

            is DataState.Result -> {
                Image(
                    bitmap = state.nnvl,
                    alignment = Alignment.Center,
                    contentDescription = null,
                    contentScale = contentScale,
                    modifier = Modifier.fillMaxSize()
                        .then(
                            if (alphaBackground) Modifier.drawAlphaBackground()
                            else Modifier
                        )
                )
            }
        }
    }
    LaunchedEffect(category, key) {
        if (Repository.files.contains(category, key)) {
            imageState.value = DataState.Result(Repository.files.fetchImage(category, key))
        } else {
            imageState.value = DataState.Loading()
            runCatching {
                Repository.files.fetchImage(category, key)
            }.onSuccess {
                if (isActive) imageState.value = DataState.Result(it)
            }.onFailure {
                logERROR(it)
                if (isActive) imageState.value = DataState.Error(it.message ?: "Fetch fail")
            }
        }
    }
}

fun Modifier.drawAlphaBackground(): Modifier = drawBehind {
    val darkColor = Color.DarkGray
    val lightColor = Color.Gray

    val gridSizePxW: Float = size.width * 0.05F
    val gridSizePxH: Float = size.height * 0.05F
    val cellCountX = (size.width / gridSizePxW).runCatching { roundToInt() }.getOrNull() ?: return@drawBehind
    val cellCountY = (size.height / gridSizePxH).runCatching { roundToInt() }.getOrNull() ?: return@drawBehind

    for (column in 0 until cellCountX) {
        for (row in 0 until cellCountY) {
            val color = if ((column + row) % 2 == 0) darkColor else lightColor

            val x = column * gridSizePxW
            val y = row * gridSizePxH

            drawRect(color, Offset(x, y), Size(gridSizePxW, gridSizePxH))
        }
    }
}
